'use client'

import { LayoutDashboard, LogOut, Crown, Gift, Plus, Code, Settings, Shield } from 'lucide-react'
import { useRouter, usePathname } from 'next/navigation'
import { useSession, signOut } from 'next-auth/react'
import { useMemo, useState } from 'react'

import { isSuperAdmin } from '@server/auth/is-superadmin'

import { AuthDialog } from '@client/components/auth/AuthDialog'
import { InviteDialog } from '@client/components/InviteDialog'
import { Avatar, AvatarFallback } from '@client/components/ui/avatar'
import { Badge } from '@client/components/ui/badge'
import { Button } from '@client/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu'
import { formatCreditsRemaining } from '@client/lib/r3-client'
import { useCredits } from '@client/providers/credits-provider'
import { useSelectedProject } from '@client/providers/project-provider'

// Global z-index for radix popper content (Select/Dropdown/Popover wrappers).
// Must stay ABOVE the header AND above dialogs (DialogContent is z-[1001]) so
// dropdowns inside a modal — e.g. role selects in the manage-members dialog —
// render on top of it rather than behind.
const popperStyle = `
  div[data-radix-popper-content-wrapper] {
    z-index: 1100!important;
  }
`

export function UserMenu() {
  const router = useRouter()
  const pathname = usePathname()
  const { data: session, status } = useSession()
  const { credits, loading: creditsLoading } = useCredits()
  const { selected } = useSelectedProject()

  const [showAuth, setShowAuth] = useState(false)
  const [showInvite, setShowInvite] = useState(false)

  const creditsDisplay = useMemo(() => {
    if (creditsLoading) return 'Loading...'
    if (!credits) return null

    if (credits.plan === 'Journal') {
      return (
        <span className="flex items-center gap-1">
          <Crown className="h-3 w-3 text-yellow-600" />
          Journal Access
        </span>
      )
    }

    if (credits.plan === 'Institutional') {
      return credits.institution?.name ?? 'Institutional Access'
    }

    if (credits.plan === 'Subscription') {
      return (
        <span className="flex items-center gap-1">
          <Crown className="h-3 w-3 text-yellow-600" />
          Unlimited Monthly
        </span>
      )
    }

    // Users start at 0 credits now, so "0 Credits Remaining" is the default
    // state, not information — show the line only when there's a balance.
    if (!credits.remaining) return null
    return formatCreditsRemaining(credits.remaining)
  }, [credits, creditsLoading])

  if (status === 'loading') {
    return <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
  }

  // Show sign-in button for anonymous users
  if (!session) {
    return (
      <>
        <Button type="button" variant="outline" size="sm" onClick={() => setShowAuth(true)}>
          Sign In
        </Button>
        <AuthDialog
          open={showAuth}
          onOpenChange={setShowAuth}
          callbackUrl={pathname || '/'}
          showReferral={true}
          onSuccess={() => {
            setShowAuth(false)
            router.push(pathname || '/')
            router.refresh()
          }}
        />
      </>
    )
  }

  // Derive user initials for avatar fallback
  const initials =
    session.user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() ||
    session.user?.email?.[0]?.toUpperCase() ||
    'U'

  return (
    <>
      <style>{popperStyle}</style>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full" data-testid="user-menu-btn">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            {credits?.plan && !credits.plan.startsWith('Free') && (
              <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-yellow-600">
                <Crown className="h-2.5 w-2.5 text-white" />
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 relative top-1" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{session.user?.name || 'User'}</p>
              <p className="text-xs leading-none text-muted-foreground">{session.user?.email}</p>
              {creditsDisplay && <p className="text-xs leading-none text-muted-foreground">{creditsDisplay}</p>}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => router.push(selected.homePath)}
            data-testid="user-menu-dashboard"
          >
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => router.push(selected.kind === 'personal' ? '/projects#new-paper' : selected.uploadHref)}
            data-testid="user-menu-new-review"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>{selected.kind === 'personal' ? 'New paper' : 'New review'}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => router.push('/api-dashboard')}
            data-testid="user-menu-api"
          >
            <Code className="mr-2 h-4 w-4" />
            <span>API</span>
          </DropdownMenuItem>
          {isSuperAdmin(session) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => router.push('/admin')}
                data-testid="user-menu-superadmin"
              >
                <Shield className="mr-2 h-4 w-4" />
                <span>Superadmin</span>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onClick={() => router.push('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => setShowInvite(true)}>
            <Gift className="mr-2 h-4 w-4" />
            <span>Invite Friends</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer text-red-600"
            onClick={() => {
              // Drop the per-browser project selection so the next sign-in starts
              // at the new user's default (not a leftover Personal or stale org).
              void signOut({ callbackUrl: '/' })
            }}
            data-testid="user-menu-sign-out"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
        <InviteDialog open={showInvite} onOpenChange={setShowInvite} />
      </DropdownMenu>
    </>
  )
}
